import { Dispatch, SetStateAction } from 'react';
import { QueryClient } from 'react-query';
import { confirmPopup } from 'primereact/confirmpopup';
import { OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../../constants/constants';
import { ADD, CUSTOM_PERMISSIONS, DEFAULT_PERMISSIONS, DELETE_PERMISSION_MSG } from '../../../../../constants/strings';
import { _PermissionGroup, _UserType } from '../../../../../constants/staticTypes';
import { JTranslation } from '../../../../../helpers/jTranslationHelper';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { checkActionSubPermission } from '../../../../../utils/common';

type _VenuePermissionSidebarProps = {
    venueGroupPermissionList: _PermissionGroup[];
    activeGroupPermission: _PermissionGroup | null;
    setActiveGroupPermission: Dispatch<SetStateAction<_PermissionGroup | null>>;
    userType: _UserType | null;
    queryClient: QueryClient;
    onAddClick: () => void;
    onDeletePermission: (permissionId: string) => void;
};

const languageTexts = {
    DEFAULT_PERMISSIONS: DEFAULT_PERMISSIONS,
    CUSTOM_PERMISSIONS: CUSTOM_PERMISSIONS,
    ADD: ADD,
};

function VenuePermissionSidebar({
    venueGroupPermissionList,
    activeGroupPermission,
    setActiveGroupPermission,
    userType,
    queryClient,
    onAddClick,
    onDeletePermission,
}: Readonly<_VenuePermissionSidebarProps>) {
    const translatedText = useTranslation(languageTexts, 'pascal');

    return (
        <div className="org-permission-body roles-link p-3 overflow-auto">
            <div className="roles-sticky-header">
                {venueGroupPermissionList?.filter((pg) => pg.isDefault).length > 0 && (
                    <>
                        <div className="d-flex align-items-center mb-3">
                            <div className="flex-grow-1">
                                <h6 className="card-title mb-0 fw-bold">
                                    <JTranslation typeCase="pascal" text={translatedText.DEFAULT_PERMISSIONS} />
                                </h6>
                            </div>
                            <div className="flex-grow-0"></div>
                        </div>
                        <ul className="list-unstyled">
                            {venueGroupPermissionList
                                ?.filter((pg) => pg.isDefault)
                                .map((item) => (
                                    <li
                                        role={activeGroupPermission?.id === item.id ? '' : 'button'}
                                        data-testid={item.id}
                                        key={item.id}
                                        className={item.id === activeGroupPermission?.id ? 'active' : ''}
                                        onClick={() => {
                                            if (item.id !== activeGroupPermission?.id) {
                                                setActiveGroupPermission(item);
                                            }
                                        }}
                                    >
                                        <JTranslation typeCase="pascal" text={item.permissionRoleName} />
                                    </li>
                                ))}
                        </ul>
                    </>
                )}

                <div className="d-flex align-items-center mt-4 mb-3">
                    <div className="flex-grow-1">
                        <h6 className="card-title mb-0 fw-bold">
                            <JTranslation typeCase="pascal" text={translatedText.CUSTOM_PERMISSIONS} />
                        </h6>
                    </div>
                    <div className="flex-grow-0">
                        {(userType === 'organizationAdmin' ||
                            checkActionSubPermission(
                                queryClient,
                                OrgPermissionKeys.MANAGE_VENUE_PERMISSION,
                                OrgSubPermissionKeys.ADD_VENUE_PERMISSION
                            )) && (
                            <button
                                type="button"
                                data-testid="add-permission-group"
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                onClick={onAddClick}
                            >
                                <JTranslation typeCase="pascal" text={translatedText.ADD} />
                                <i className="ri-add-line"></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div>
                <ul className="list-unstyled">
                    {venueGroupPermissionList
                        ?.filter((pg) => !pg.isDefault)
                        .map((item) => (
                            <li
                                key={item.id}
                                className={`d-flex align-items-center ${item.id === activeGroupPermission?.id ? 'active' : ''}`}
                                role={item.id !== activeGroupPermission?.id ? 'button' : ''}
                            >
                                <div
                                    role={item.id !== activeGroupPermission?.id ? 'button' : ''}
                                    data-test-filed={item.id}
                                    key={item.id}
                                    className="flex-grow-1"
                                    onClick={() => {
                                        if (item.id !== activeGroupPermission?.id) {
                                            setActiveGroupPermission(item);
                                        }
                                    }}
                                >
                                    {item.permissionRoleName ?? ''}
                                </div>
                                <div className="flox-grow-0">
                                    {(userType === 'organizationAdmin' ||
                                        checkActionSubPermission(
                                            queryClient,
                                            OrgPermissionKeys.MANAGE_VENUE_PERMISSION,
                                            OrgSubPermissionKeys.DELETE_VENUE_PERMISSION
                                        )) && (
                                        <span>
                                            <i
                                                data-testid={item.id + '#delete'}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    confirmPopup({
                                                        target: e.currentTarget,
                                                        message: (
                                                            <JTranslation type="html" text={DELETE_PERMISSION_MSG} typeCase="capitalize" />
                                                        ),
                                                        icon: 'pi pi-info-circle',
                                                        defaultFocus: 'reject',
                                                        accept: () => onDeletePermission(item?.id),
                                                        reject: () => {},
                                                    });
                                                }}
                                                className="ri-delete-bin-6-line"
                                                style={{ cursor: 'pointer' }}
                                            ></i>
                                        </span>
                                    )}
                                </div>
                            </li>
                        ))}
                </ul>
            </div>
        </div>
    );
}

export default VenuePermissionSidebar;
